import type { Subscription } from '../../../../../types';

import * as styles from './index.css';
import RadioButtonGroup from '@blueshift-ui/inputs/dist/components/radio-button-group';
import RichTranslation from '@blueshift-ui/i18n/dist/components/rich-translation';
import Stack from '@blueshift-ui/core/dist/components/stack';
import SubscriptionAction from '../../views/options/subscription-action';
import formatNumber from '../../../../../helpers/format-number';
import getProgramDescriptionTranslationKeys from '../../../../../helpers/your-program/get-program-description-translation-keys';
import { useState } from 'react';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';

interface SubscriptionOptionProps {
  checked: boolean;
  description: React.ReactNode;
  duration: React.ReactNode;
  price: React.ReactNode;
  supplementalRate?: React.ReactNode;
  title?: React.ReactNode;
}

function _SubscriptionOption({
  checked,
  description,
  duration,
  price,
  supplementalRate,
  title,
}: SubscriptionOptionProps) {
  return (
    <div className={styles.subscriptionOptionContainer}>
      {title && <label className={styles.label}>{title}</label>}
      <div
        className={`${styles.subscriptionOption} ${checked && styles.checkedSubscriptionOption}`}
      >
        <div className={styles.plan}>
          <div className={styles.duration}>{duration}</div>
          <div className={styles.price}>{price}</div>
          <div className={styles.supplementalPrice}>{supplementalRate}</div>
        </div>
        <div className={styles.description}>{description}</div>
      </div>
    </div>
  );
}

interface GetOptionUnavailableKeyParams {
  subscriptionAction: SubscriptionAction;
  subscriptionOption: Subscription;
}

function _getOptionUnavailableKey({
  subscriptionAction,
  subscriptionOption,
}: GetOptionUnavailableKeyParams) {
  const subscriptionType = subscriptionOption.is_contract ? 'monthly' : 'contract';
  const key = subscriptionAction === SubscriptionAction.adjust ? 'adjust' : subscriptionType;

  return `${key}_option_unavailable`;
}

interface GetDurationParams {
  hours?: number;
  isContract: boolean;
  periods: number;
}

interface UpdateMembershipStep2Props {
  onChange: (option: Subscription) => void;
  subscriptionAction: number;
  subscriptionOptions: Subscription[];
}

function UpdateMembershipStep2({
  onChange,
  subscriptionAction,
  subscriptionOptions,
}: UpdateMembershipStep2Props) {
  const { loading: loadingRichTranslation, translate: richTranslate } = useTranslation(
    'update.membership.options',
    { rich: true }
  );
  const { loading: loadingTranslation, translate } = useTranslation('update.membership.options');

  const [checkedOption, setCheckedOption] = useState(subscriptionOptions[0].item_id);

  if (loadingTranslation || loadingRichTranslation) {
    return null;
  }

  function getDuration({ hours, isContract, periods }: GetDurationParams) {
    if (hours === 0) {
      return translate('pay_as_you_go');
    }

    if (subscriptionAction === SubscriptionAction.adjust) {
      return `${hours} ${translate('monthly_hours')}`;
    }

    return isContract
      ? `${periods} ${translate('contract_duration')}`
      : translate('monthly_duration');
  }

  function getSupplementalRate(
    { supplemental_rate_cents_per_hour, currency_code }: Partial<Subscription>,
    withTutoringHours: boolean
  ) {
    if (!supplemental_rate_cents_per_hour) {
      return '';
    }

    const formattedSupplementalRate = formatNumber({
      currency: currency_code,
      number: supplemental_rate_cents_per_hour / 100,
    });
    const translationKey = withTutoringHours ? 'per_additional_tutoring_hour' : 'per_tutoring_hour';

    return `${formattedSupplementalRate} ${translate(translationKey)}`;
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const subscriptionOption = subscriptionOptions.find(
      ({ item_id }) => item_id === event.target.value
    );

    setCheckedOption(event.target.value);

    if (subscriptionOption) {
      onChange(subscriptionOption);
    }
  }

  return (
    <>
      <RichTranslation i18nKey="step_2" translator={richTranslate} />

      <Stack direction={{ xs: 'column' }}>
        <RadioButtonGroup
          className={styles.radioButtonGroup}
          defaultValue={subscriptionOptions[0].item_id}
          inputOptions={subscriptionOptions.map((subscription, index) => {
            const {
              contract: { duration_periods },
              currency_code,
              discounted_price_cents,
              is_contract,
              item_id,
              purchase_price_cents,
              entitlements,
            } = subscription;
            const title = index === 0 ? translate('current_plan') : undefined;
            const tutoringHoursEntitlement = entitlements.find(
              ({ type }) => type === 'tutoring_hours'
            );
            const number_of_hours = tutoringHoursEntitlement?.configuration?.number_of_hours;
            const withTutoringHours = number_of_hours !== undefined && number_of_hours > 0;
            const price = formatNumber({
              currency: currency_code,
              number: (discounted_price_cents || purchase_price_cents) / 100,
            });

            return {
              label: (
                <_SubscriptionOption
                  checked={checkedOption === item_id}
                  description={
                    <RichTranslation
                      i18nKey={getProgramDescriptionTranslationKeys({
                        hours: number_of_hours,
                        isContract: is_contract,
                        subscriptionAction,
                      })}
                      replace={{ duration: duration_periods }}
                      translator={richTranslate}
                    />
                  }
                  duration={getDuration({
                    hours: number_of_hours,
                    isContract: is_contract,
                    periods: duration_periods,
                  })}
                  price={`${price}/${translate('price_duration')}`}
                  supplementalRate={getSupplementalRate(subscription, withTutoringHours)}
                  title={title}
                />
              ),
              value: item_id,
            };
          })}
          onChange={handleChange}
          variant="default"
        />

        {subscriptionOptions.length === 1 && (
          <div className={styles.optionUnavailable}>
            <RichTranslation
              i18nKey={_getOptionUnavailableKey({
                subscriptionAction,
                subscriptionOption: subscriptionOptions[0],
              })}
              translator={richTranslate}
            />
          </div>
        )}
      </Stack>
    </>
  );
}

export default UpdateMembershipStep2;
